import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import { Formik, useFormikContext } from 'formik';
import { CancelButton, SubmitButton } from '../../Form';
import { ModalActions, ModalTitle } from '../../Modal';
import { Alert } from '../../Alert';
import { useUpdateIdealCaseload } from './AvailabilitySettings.hooks';
import { useCurrentuser } from '@uplift/contexts';
import { MaskedInput, MaskedInputProps } from '../../Inputs';
import { ModalFullPage, ModalFullPageContent } from '../../ModalFullPage';
import { useIsMobile } from '@uplift/hooks';
import { useProviderScheduleQuery } from '@uplift/queries';


export type EditIdealCaseloadModalProps = {
  open: boolean;
  onClose: () => void;
};


const useStyles = makeStyles()({
  input: {
    '& input': {
      textAlign: 'center',
    }
  },
});


const schema = Yup.object().shape({
  caseload: Yup.number().transform(val => parseInt(val || '0')).min(1, ' ').required(' '),
});


export const IdealCaseloadInput: React.FC<{ name: string; color?: MaskedInputProps['color'] }> = (props) => {
  const { classes } = useStyles();

  return (
    <MaskedInput
      slotProps={{
        htmlInput: {
          inputMode: 'numeric',
        }
      }}
      color={props.color}
      mask="00"
      name={props.name}
      autoFocus
      autoComplete="off"
      className={classes.input}
    />
  );
};

export const IdealCaseloadAlerts: React.FC<{ caseload: string | null }> = ({ caseload }) => {
  const isCaseloadHours = caseload != null && caseload !== '';
  const caseloadHours = isCaseloadHours ? parseInt(caseload) : 0;

  return (
    <>
      <Collapse in={isCaseloadHours && caseloadHours >= 1}>
        <Box mt={4}>
          <Alert severity="info">
            <Typography>
              We recommend you have at least <b>{caseloadHours * 1.5} hours</b> of matching availability set up to reach your ideal weekly caseload. You can reduce your matching availability once you’ve met your goal.
            </Typography>
          </Alert>
        </Box>
      </Collapse>
      <Collapse in={isCaseloadHours && caseloadHours === 0}>
        <Box mt={4}>
          <Alert severity="error">
            <Typography>
              Your ideal weekly caseload should be more than 0.
            </Typography>
          </Alert>
        </Box>
      </Collapse>
    </>
  );
};


export const EditIdealCaseloadForm: React.FC<{ open: boolean; onClose: () => void }> = (props) => {
  const isMobile = useIsMobile();
  const formik = useFormikContext<{ caseload: string }>();

  const formBody = React.useMemo(() => {
    if (isMobile) {
      return (
        <Box display="flex" gap="24px" alignItems="center" flexDirection="column">
          <Typography variant="h4">I want to meet around</Typography>
          <Box component="span" width="50px" position="relative">
            <IdealCaseloadInput name="caseload" />
          </Box>
          <Typography variant="h4">clients every week.</Typography>
        </Box>
      );
    }

    return (
      <Box display="flex" gap="8px" alignItems="center" mb={1}>
        I want to meet around <Box component="span" width="50px" position="relative">
          <IdealCaseloadInput name="caseload" />
        </Box> clients every week.
      </Box>
    );
  }, [ isMobile ]);

  return (
    <ModalFullPage maxWidth="sm" open={props.open} onClose={props.onClose} mobileOnly transition onExited={() => formik.resetForm()}>
      <ModalTitle onClose={props.onClose}>
        Edit ideal caseload
      </ModalTitle>
      <ModalFullPageContent dividers={isMobile}>
        {formBody}
        <IdealCaseloadAlerts caseload={formik.values.caseload} />
      </ModalFullPageContent>
      {isMobile ? (
        <ModalActions>
          <SubmitButton disabled={!formik.isValid} color="secondary" fullWidth>Save</SubmitButton>
        </ModalActions>
      ) : (
        <ModalActions>
          <CancelButton onClick={props.onClose}>Close</CancelButton>
          <SubmitButton disabled={!formik.isValid} color="secondary">Save</SubmitButton>
        </ModalActions>
      )}
    </ModalFullPage>
  );
};


export const EditIdealCaseloadModal: React.FC<EditIdealCaseloadModalProps> = (props) => {
  const me = useCurrentuser();
  const { data: idealCaseload } = useProviderScheduleQuery(me.id);
  const updateIdealCaseload = useUpdateIdealCaseload();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        caseload: idealCaseload?.idealCaseload ? idealCaseload.idealCaseload.toString() : '',
      }}
      validationSchema={schema}
      onSubmit={async (values) => {
        await updateIdealCaseload({
          userId: me.id,
          caseload: parseInt(values.caseload),
        });
        props.onClose();
      }}
    >
      <EditIdealCaseloadForm
        open={props.open}
        onClose={props.onClose}
      />
    </Formik>
  );
};
